import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { eyebrowClass, cardClass } from '@/lib/sectionStyles';

const DIGITAL_WORKERS = [
  {
    name: 'Athena',
    role: 'Operations Agent',
    description:
      'FNOL and submission intake, digitization, triage and assignment. Turns unstructured intake into structured, routed work.',
  },
  {
    name: 'Atlas',
    role: 'Underwriting Agent',
    description:
      'Risk assessment, quote and issuance. Full straight-through on personal lines and small commercial; guided-to-decision on complex risks.',
  },
  {
    name: 'Bella',
    role: 'Adjuster Agent',
    description:
      'Low-complexity claims handled straight through; complex claims guided to a decision an adjuster approves. Salvage, subrogation, and SIU flags surfaced, not buried.',
  },
];

const WORK_FEATURES = [
  {
    lead: 'Work queue:',
    rest: 'SLA-sorted, with reassignment and override in operator hands',
  },
  {
    lead: 'HITL approvals:',
    rest: 'high-stakes actions park for a person and resume on decision',
  },
  {
    lead: 'Full context:',
    rest: 'every task carries its decision lineage, so reviewers see the why, not just the what',
  },
];

export function ConsolePage() {
  return (
    <>
      <Helmet>
        <title>Agent Console - CriticalBridge.AI</title>
        <meta
          name="description"
          content="Agent Console is the P&C workbench: claims from FNOL to resolution, underwriting from submission to issue, one environment over your core systems. Digital workers clear the busywork; your people carry the judgment; every handoff is governed."
        />
        <meta property="og:title" content="Agent Console - CriticalBridge.AI" />
        <meta
          property="og:description"
          content="Where adjusters, underwriters, and agents work the same queue."
        />
        <link rel="canonical" href="https://www.criticalbridge.ai/console" />
      </Helmet>

      {/* Hero */}
      <section className="bg-secondary">
        <div className="container mx-auto px-4 py-16 sm:py-20">
          <div className="font-mono text-xs text-secondary-foreground/60">
            <Link to="/" className="hover:text-secondary-foreground">
              Home
            </Link>{' '}
            / Products / Agent Console
          </div>
          <span className="mt-4 block font-mono text-xs font-semibold uppercase tracking-widest text-primary">
            WORK
          </span>
          <h1 className="mt-2 max-w-[26ch] text-4xl font-extrabold leading-tight text-secondary-foreground sm:text-5xl">
            Where adjusters, underwriters, and agents work the same queue.
          </h1>
          <p className="mt-4 max-w-2xl text-lg leading-relaxed text-secondary-foreground/70">
            Agent Console is the P&amp;C workbench: claims from FNOL to resolution, underwriting
            from submission to issue, one environment over your core systems. Digital workers clear
            the busywork; your people carry the judgment; every handoff is governed.
          </p>
          <Button size="lg" className="mt-8" asChild>
            <Link to="/contact">Book a working session</Link>
          </Button>
        </div>
      </section>

      {/* The digital workers */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <span className={eyebrowClass}>The digital workers</span>
          <h2 className="mt-2 text-3xl font-bold">Meet the team you don't have to hire.</h2>

          <div className="mt-8 grid gap-4 sm:grid-cols-3">
            {DIGITAL_WORKERS.map((worker) => (
              <div key={worker.name} className={cardClass}>
                <div className="text-lg font-bold">{worker.name}</div>
                <div className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground">
                  {worker.role}
                </div>
                <p className="mt-2 text-sm text-muted-foreground">{worker.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Where people meet agents */}
      <section className="border-y bg-card py-16">
        <div className="container mx-auto px-4">
          <span className={eyebrowClass}>Where people meet agents</span>
          <h2 className="mt-2 text-3xl font-bold">Work doesn't happen in a dashboard.</h2>
          <p className="mt-4 max-w-[68ch] text-muted-foreground">
            The mesh routes agent conversations across Slack, Microsoft Teams, Google Chat, and the
            in-app panel — ask the concierge what the fleet can do, delegate a task to a named
            agent, and get the answer back in the thread, with the same policy gates and lineage as
            any other invocation. Human approvals arrive where the approver already works.
          </p>

          <ul className="mt-4 grid max-w-[74ch] gap-2.5">
            {WORK_FEATURES.map((item) => (
              <li key={item.lead} className="relative pl-6 text-base text-muted-foreground">
                <span className="absolute left-0 top-2 h-2.5 w-2.5 rounded-sm bg-primary" />
                <strong className="text-foreground">{item.lead}</strong> {item.rest}
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Economics */}
      <section className="bg-muted py-16">
        <div className="container mx-auto px-4">
          <span className={eyebrowClass}>Economics</span>
          <h2 className="mt-2 text-3xl font-bold">
            Priced by the outcome. Accretive on every unit.
          </h2>
          <p className="mt-4 max-w-[68ch] text-muted-foreground">
            Digital workers are priced per completed unit of work — an intake, a straight-through
            claim, a decisioned submission — not per agent and not per seat. Illustrative mid-size
            carrier modeling puts per-unit savings at roughly 75–80% across intake, straight-through
            claims, straight-through submissions, and guided complex work.
          </p>
          <Button variant="outline" className="mt-6" asChild>
            <Link to="/contact">Model it for your book →</Link>
          </Button>
        </div>
      </section>
    </>
  );
}
